import { useEffect } from 'react';

/**
 * Keyboard shortcuts for tool selection and tab switching
 *
 * Tool Shortcuts:
 * - S: Select Tool (industry standard)
 * - R: Rectangle Tool
 * - O: Circle/Ellipse Tool (O for Oval)
 * - L: Line Tool (future)
 * - P: Polygon Tool (future)
 * - B: Bezier Tool (future)
 * - Esc: Cancel/Deselect (switch to select tool)
 *
 * Tab Shortcuts:
 * - 1: Zeichnen Tab
 * - 2: Garten Tab
 * - 3: Pflanzen Tab
 */

// Keyboard shortcut mapping
const TOOL_SHORTCUTS = {
  's': 'select',
  'r': 'rectangle',
  'o': 'circle',
  'l': 'line',
  'p': 'polygon',
  'b': 'curve', // Bezier curve tool (registered as 'curve' in MainLayout)
  'escape': 'select', // Cancel/deselect
};

/**
 * Custom hook for managing keyboard shortcuts for tab switching
 *
 * @param {Function} onTabChange - Callback function to change active tab
 *
 * @example
 * ```jsx
 * function MainLayout() {
 *   const [activeTab, setActiveTab] = useState('garten');
 *   useTabShortcuts(setActiveTab);
 *   // ...
 * }
 * ```
 */
export const useTabShortcuts = (onTabChange) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore shortcuts when typing in input fields or textareas
      if (
        e.target.tagName === 'INPUT' ||
        e.target.tagName === 'TEXTAREA' ||
        e.target.isContentEditable
      ) {
        return;
      }

      // Tab shortcuts: 1, 2
      const tabMapping = {
        '1': 'garten',
        '2': 'pflanzen',
      };

      const tab = tabMapping[e.key];

      if (tab) {
        onTabChange(tab);
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onTabChange]);
};

/**
 * Custom hook for managing keyboard shortcuts for tool selection
 *
 * @param {Function} onToolChange - Callback function to change active tool
 * @param {string} currentTool - Currently active tool (optional, for future use)
 * @param {string} activeTab - Currently active tab (garten, pflanzen)
 * @param {Array} availableTools - Array of tool IDs available in current tab
 *
 * @example
 * ```jsx
 * function MainLayout() {
 *   const [activeTool, setActiveTool] = useState('select');
 *   const [activeTab, setActiveTab] = useState('garten');
 *   const availableTools = ['select', 'pan', 'rectangle', 'circle'];
 *   useKeyboardShortcuts(setActiveTool, activeTool, activeTab, availableTools);
 *   // ...
 * }
 * ```
 */
export const useKeyboardShortcuts = (onToolChange, currentTool, activeTab, availableTools) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore shortcuts when typing in input fields or textareas
      if (
        e.target.tagName === 'INPUT' ||
        e.target.tagName === 'TEXTAREA' ||
        e.target.isContentEditable
      ) {
        return;
      }

      // Get the tool mapped to this key
      const key = e.key.toLowerCase();
      const tool = TOOL_SHORTCUTS[key];

      // Only trigger if tool is mapped AND available in current tab
      if (tool && availableTools && availableTools.includes(tool)) {
        // Change to the mapped tool
        onToolChange(tool);

        // Prevent browser default shortcuts
        e.preventDefault();
      }
    };

    // Add event listener
    window.addEventListener('keydown', handleKeyDown);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onToolChange, availableTools]);
};

/**
 * Get the keyboard shortcut for a specific tool
 * Useful for displaying shortcuts in UI (tooltips, buttons, etc.)
 *
 * @param {string} toolName - Name of the tool
 * @returns {string|null} - Keyboard shortcut key (uppercase) or null
 *
 * @example
 * ```jsx
 * const shortcut = getShortcutForTool('rectangle'); // Returns 'R'
 * ```
 */
export const getShortcutForTool = (toolName) => {
  const entry = Object.entries(TOOL_SHORTCUTS).find(
    ([key, tool]) => tool === toolName && key !== 'escape'
  );
  return entry ? entry[0].toUpperCase() : null;
};

/**
 * Get all available keyboard shortcuts
 * Useful for help dialogs or documentation
 *
 * @returns {Array<{key: string, tool: string}>} - Array of shortcut mappings
 *
 * @example
 * ```jsx
 * const shortcuts = getAllShortcuts();
 * // [{ key: 'S', tool: 'select' }, { key: 'R', tool: 'rectangle' }, ...]
 * ```
 */
export const getAllShortcuts = () => {
  return Object.entries(TOOL_SHORTCUTS)
    .filter(([key]) => key !== 'escape')
    .map(([key, tool]) => ({
      key: key.toUpperCase(),
      tool,
    }));
};

export default useKeyboardShortcuts;
